"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { SITE } from "@/lib/constants";

export function BookingWidget() {
  const router = useRouter();
  const [visible, setVisible] = useState(false);
  const [checkIn, setCheckIn] = useState<Date>();
  const [checkOut, setCheckOut] = useState<Date>();

  const handleBookNow = () => {
    const params = new URLSearchParams();
    if (checkIn) params.set("checkIn", format(checkIn, "yyyy-MM-dd"));
    if (checkOut) params.set("checkOut", format(checkOut, "yyyy-MM-dd"));
    const query = params.toString();
    router.push(`/book${query ? `?${query}` : ""}`);
  };

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > window.innerHeight);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-50 border-t border-border glass"
        >
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-8">
            {/* Price */}
            <div className="shrink-0">
              <p className="text-xs text-muted-foreground">From</p>
              <p className="text-lg font-bold text-foreground md:text-xl">
                <span className="text-primary">
                  ₹{SITE.basePricePerNight.toLocaleString("en-IN")}
                </span>
                <span className="text-sm font-normal text-muted-foreground">
                  /night
                </span>
              </p>
            </div>

            {/* Date Pickers — hidden on mobile */}
            <div className="hidden items-center gap-3 md:flex">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "w-40 justify-start gap-2 border-border text-left font-normal",
                      !checkIn && "text-muted-foreground"
                    )}
                  >
                    <CalendarDays className="size-3.5 text-primary" />
                    {checkIn
                      ? format(checkIn, "MMM dd")
                      : "Check-in"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" side="top">
                  <Calendar
                    mode="single"
                    selected={checkIn}
                    onSelect={setCheckIn}
                    disabled={(date) => date < new Date()}
                  />
                </PopoverContent>
              </Popover>

              <span className="text-muted-foreground">—</span>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "w-40 justify-start gap-2 border-border text-left font-normal",
                      !checkOut && "text-muted-foreground"
                    )}
                  >
                    <CalendarDays className="size-3.5 text-primary" />
                    {checkOut
                      ? format(checkOut, "MMM dd")
                      : "Check-out"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" side="top">
                  <Calendar
                    mode="single"
                    selected={checkOut}
                    onSelect={setCheckOut}
                    disabled={(date) =>
                      date < (checkIn || new Date())
                    }
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Book Now */}
            <Button
              onClick={handleBookNow}
              className="shrink-0 rounded-full bg-primary px-6 font-semibold text-primary-foreground hover:bg-primary/90 gold-glow md:px-8"
            >
              Book Now
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
